import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, Calculator, Search } from "lucide-react";

const API = "/api";

export default function Teachers() {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    rate_25min: "40",
    rate_30min: "50",
    additional_rates: [],
  });

  useEffect(() => {
    fetchTeachers();
  }, []);

  const fetchTeachers = async () => {
    try {
      const response = await axios.get(`${API}/teachers`);
      setTeachers(response.data);
    } catch (error) {
      console.error("Failed to fetch teachers:", error);
      toast.error("Failed to load teachers");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (teacher = null) => {
    if (teacher) {
      setSelectedTeacher(teacher);
      setFormData({
        name: teacher.name,
        rate_25min: teacher.rate_25min.toString(),
        rate_30min: teacher.rate_30min.toString(),
        additional_rates: teacher.additional_rates || [],
      });
    } else {
      setSelectedTeacher(null);
      setFormData({ name: "", rate_25min: "40", rate_30min: "50", additional_rates: [] });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        rate_25min: parseFloat(formData.rate_25min),
        rate_30min: parseFloat(formData.rate_30min),
        additional_rates: formData.additional_rates,
      };

      if (selectedTeacher) {
        await axios.put(`${API}/teachers/${selectedTeacher.id}`, payload);
        toast.success("Teacher updated");
      } else {
        await axios.post(`${API}/teachers`, payload);
        toast.success("Teacher added");
      }
      setDialogOpen(false);
      fetchTeachers();
    } catch (error) {
      console.error("Failed to save teacher:", error);
      toast.error("Failed to save teacher");
    }
  };

  const handleDelete = async () => {
    if (!selectedTeacher) return;
    try {
      await axios.delete(`${API}/teachers/${selectedTeacher.id}`);
      toast.success("Teacher deleted");
      setDeleteDialogOpen(false);
      setSelectedTeacher(null);
      fetchTeachers();
    } catch (error) {
      console.error("Failed to delete teacher:", error);
      toast.error("Failed to delete teacher");
    }
  };

  const filteredTeachers = teachers.filter((teacher) =>
    teacher.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="teachers-loading">
        <div className="animate-pulse text-slate-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="teachers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Teachers</h1>
          <p className="text-slate-500 mt-1">Manage teacher profiles and rates</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-indigo-600 hover:bg-indigo-700 btn-active"
          data-testid="add-teacher-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Teacher
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search teachers..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
          data-testid="search-teachers-input"
        />
      </div>

      {/* Teachers Table */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="font-semibold text-slate-600">Teacher</TableHead>
                <TableHead className="font-semibold text-slate-600 text-center">25-min Rate</TableHead>
                <TableHead className="font-semibold text-slate-600 text-center">30-min Rate</TableHead>
                <TableHead className="font-semibold text-slate-600 text-center">Custom Rates</TableHead>
                <TableHead className="font-semibold text-slate-600 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTeachers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-12 text-slate-500">
                    {searchQuery ? "No teachers found" : "No teachers yet. Add one to get started."}
                  </TableCell>
                </TableRow>
              ) : (
                filteredTeachers.map((teacher) => (
                  <TableRow
                    key={teacher.id}
                    className="table-row-hover"
                    data-testid={`teacher-row-${teacher.id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                          <span className="text-teal-700 font-semibold text-sm">
                            {teacher.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                          </span>
                        </div>
                        <span className="font-medium text-slate-900">{teacher.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center font-medium">
                      ₱{teacher.rate_25min.toFixed(0)}
                    </TableCell>
                    <TableCell className="text-center font-medium">
                      ₱{teacher.rate_30min.toFixed(0)}
                    </TableCell>
                    <TableCell className="text-center">
                      {(teacher.additional_rates || []).length > 0 ? (
                        <div className="flex flex-wrap gap-1 justify-center">
                          {teacher.additional_rates.map(r => (
                            <span key={r.name} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-200">
                              {r.name}: ₱{r.rate}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-slate-400 text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Link to={`/salary-tracker/${teacher.id}`}>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-slate-600 hover:text-teal-600"
                            data-testid={`salary-tracker-${teacher.id}`}
                          >
                            <Calculator className="w-4 h-4" />
                          </Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(teacher)}
                          className="text-slate-600 hover:text-indigo-600"
                          data-testid={`edit-teacher-${teacher.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedTeacher(teacher);
                            setDeleteDialogOpen(true);
                          }}
                          className="text-slate-600 hover:text-red-600"
                          data-testid={`delete-teacher-${teacher.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="teacher-dialog" aria-describedby="teacher-dialog-description">
          <DialogHeader>
            <DialogTitle>
              {selectedTeacher ? "Edit Teacher" : "Add New Teacher"}
            </DialogTitle>
            <p id="teacher-dialog-description" className="text-sm text-slate-500">
              {selectedTeacher ? "Update teacher information and rates" : "Add a new teacher with their pay rates"}
            </p>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Teacher Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., T. Clarisse"
                  required
                  data-testid="teacher-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="rate25">25-min Class Rate (₱)</Label>
                  <Input
                    id="rate25"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.rate_25min}
                    onChange={(e) => setFormData({ ...formData, rate_25min: e.target.value })}
                    required
                    data-testid="teacher-rate25-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rate30">30-min Class Rate (₱)</Label>
                  <Input
                    id="rate30"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.rate_30min}
                    onChange={(e) => setFormData({ ...formData, rate_30min: e.target.value })}
                    required
                    data-testid="teacher-rate30-input"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                data-testid="cancel-teacher-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="save-teacher-btn"
              >
                {selectedTeacher ? "Update" : "Add"} Teacher
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="delete-teacher-dialog" aria-describedby="delete-teacher-description">
          <DialogHeader>
            <DialogTitle>Delete Teacher</DialogTitle>
          </DialogHeader>
          <p id="delete-teacher-description" className="text-slate-600 py-4">
            Are you sure you want to delete <strong>{selectedTeacher?.name}</strong>? This will also
            delete all their salary records. This action cannot be undone.
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              data-testid="cancel-delete-teacher-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-teacher-btn"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
